import React, { useEffect, useState } from 'react';
import { DHReferenceLocal } from 'domains/references';
import { SparePartsViewType } from 'domains/user';
import { ProductDescription } from 'pages/CatalogPage/DH/Product/ProductDescription';
import type { ProductDetailProps } from 'pages/CatalogPage/DH/Product/ProductTab';
import { Box, Flex } from 'UI';
import { useLarge } from 'utils';
import { ProductExpandedImageView } from './ProductExpandedImageView';
import { ProductImageGallery } from './ProductImageGallery';
import { NormalWidePanel, SlidePanel, SlideStage, TopSectionContainer } from './ProductTopSection.styled';
import { useProductGallery } from './useProductGallery';

interface ProductTopSectionProps extends ProductDetailProps {
  imageFooter?: React.ReactNode;
  isDiscontinued?: boolean;
  refLocal: DHReferenceLocal;
  sparePartsView: SparePartsViewType;
}

export const ProductTopSection = ({ imageFooter, isDiscontinued, ...productProps }: ProductTopSectionProps) => {
  const { refLocal, sparePartsView } = productProps;
  const gallery = useProductGallery(refLocal);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const isWide = useLarge();

  useEffect(() => {
    if (selectedIndex >= gallery.assets.length && gallery.assets.length > 0) {
      setSelectedIndex(gallery.assets.length - 1);
    }
  }, [gallery.assets.length, selectedIndex]);

  const handleOpenExpanded = (index: number) => {
    setSelectedIndex(index);
    if (gallery.assets[index]?.type === '3d') {
      gallery.load3dModel();
    }
    setExpanded(true);
  };

  const imageSection = (
    <Flex direction={'column'} minWidth={0}>
      <ProductImageGallery
        gallery={gallery}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        sparePartsView={sparePartsView}
        isDiscontinued={isDiscontinued}
        onOpenExpanded={handleOpenExpanded}
      />
      {imageFooter}
    </Flex>
  );

  const descriptionSection = (
    <Flex minWidth={200} maxWidth={600}>
      <ProductDescription {...productProps} />
    </Flex>
  );

  const expandedSection = (
    <ProductExpandedImageView
      gallery={gallery}
      isDiscontinued={isDiscontinued}
      refLocal={refLocal}
      selectedIndex={selectedIndex}
      setSelectedIndex={setSelectedIndex}
      sparePartsView={sparePartsView}
      onClose={() => setExpanded(false)}
      onOpenExpanded={handleOpenExpanded}
    />
  );

  const renderSlidingPanels = (normalSection: React.ReactNode) => (
    <TopSectionContainer>
      <SlideStage>
        <SlidePanel
          $active={!expanded}
          $placement={'normal'}
          aria-hidden={expanded}
          data-testid={'product-top-normal-panel'}
        >
          {normalSection}
        </SlidePanel>
        <SlidePanel
          $active={expanded}
          $placement={'expanded'}
          $withCardPadding={expanded}
          aria-hidden={!expanded}
          data-testid={'product-top-expanded-panel'}
        >
          {expandedSection}
        </SlidePanel>
      </SlideStage>
    </TopSectionContainer>
  );

  if (!isWide) {
    return (
      <Flex direction={'column'}>
        {renderSlidingPanels(imageSection)}
        <Box height={24} />
        {descriptionSection}
      </Flex>
    );
  }

  return renderSlidingPanels(
    <NormalWidePanel wrap={'wrap'} data-cy={'dh-reference-display'}>
      {imageSection}
      <Flex size={0} minWidth={60} />
      {descriptionSection}
    </NormalWidePanel>,
  );
};
